import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Edit3, Sparkles, X } from '@/components/icons';
import {
  AgentActivity,
  Message,
  MessageBubble,
  MessageBubbleContent,
  MessageContent,
  MessageScroller,
  PromptInput,
  ReasoningText,
  StreamingResponse,
  Tooltip,
} from '@/components/ui/beui';
import { SPRING_LAYOUT } from '@/components/ui/beui/lib/ease';
import {
  AI_PROVIDERS,
  getDefaultProvider,
  getSelectedCustomModel,
  setSelectedCustomModel,
} from '../../services/apiKeyService';
import type { AIProvider } from '../../services/apiKeyService';
import { buildActivityItems, IDLE_PHRASES } from './activityItems';
import {
  buildProviderModels,
  decodeModelSelection,
  encodeModelSelection,
  shortModelName,
} from './providers';
import { SUGGESTIONS } from './suggestions';
import type { ChatMessage } from './types';

export interface AIPanelProps {
  open: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  isGenerating: boolean;
  /** True when the canvas already holds a diagram (drives copy + activity labels). */
  hasDiagram: boolean;
  onSend: (instruction: string, provider?: AIProvider, modelId?: string) => void;
  onStop: () => void;
  onRetry: (message: ChatMessage) => void;
  /** Drop the whole transcript and start over; stops any in-flight turn. */
  onNewChat: () => void;
  /**
   * Dock slot from the editor shell (EditorLayout). When given, the panel
   * portals there so it sits outside the editor card, beside the shrunken
   * shell — the shell owns the slide-in animation. Without it (demo), the
   * panel docks inline as a plain flex sibling of the canvas.
   */
  container?: HTMLElement | null;
}

const PANEL_WIDTH = 380;

/** "Firebase Auth, Firestore +2 more" — label list capped for one line. */
function labelList(labels: string[] | undefined, count: number, max = 3): string {
  if (!labels || labels.length === 0) return `${count}`;
  if (labels.length <= max) return labels.join(', ');
  return `${labels.slice(0, max).join(', ')} +${labels.length - max} more`;
}

/** Model-authored prose, capped for the reply line. */
function capNotes(notes: string): string {
  return notes.length > 160 ? `${notes.slice(0, 157)}…` : notes;
}

/**
 * Deterministic counts line — doubles as the compact activity-collapse header,
 * which truncates by design, so it must stay short.
 */
export function buildCountsSummary(message: ChatMessage): string {
  const counts = message.mergeCounts;
  if (message.mode === 'revise' && counts) {
    const changed = counts.changed ?? counts.updated;
    const parts: string[] = [];
    const part = (verb: string, count: number, noun: string) =>
      `${verb} ${count} ${noun}${count === 1 ? '' : 's'}`;
    if (counts.added > 0) parts.push(`Added ${labelList(counts.addedLabels, counts.added)}`);
    if (changed > 0) parts.push(`Updated ${changed}`);
    if (counts.removed > 0) parts.push(`Removed ${labelList(counts.removedLabels, counts.removed)}`);
    if (counts.edgesAdded) parts.push(part('Added', counts.edgesAdded, 'connection'));
    if (counts.edgesRemoved) parts.push(part('Removed', counts.edgesRemoved, 'connection'));
    if (counts.groupsAdded) parts.push(part('Added', counts.groupsAdded, 'group'));
    if (counts.groupsRemoved) parts.push(part('Removed', counts.groupsRemoved, 'group'));
    if (counts.groupsUpdated) parts.push(part('Updated', counts.groupsUpdated, 'group'));
    if (parts.length === 0) return 'No changes needed — your diagram already matches.';
    return parts.join(' · ');
  }
  const added = counts?.added ?? 0;
  const noun = added === 1 ? 'service' : 'services';
  const verb = message.mode === 'replace' ? 'Rebuilt the diagram with' : 'Generated';
  return `${verb} ${added} ${noun}`;
}

/** What a completed turn says as its reply: the model's own words, else counts. */
function buildReply(message: ChatMessage): string {
  const notes = message.replyText?.trim();
  return notes ? capNotes(notes) : buildCountsSummary(message);
}

const EMPTY_EVENTS: ChatMessage['activity'] = [];

const AssistantTurn: React.FC<{
  message: ChatMessage;
  hasDiagram: boolean;
  onRetry: () => void;
}> = ({ message, hasDiagram, onRetry }) => {
  const providerName = message.provider
    ? message.provider === 'custom' && message.modelId
      ? shortModelName(message.modelId)
      : AI_PROVIDERS[message.provider].name
    : 'AI';
  const events = message.activity ?? EMPTY_EVENTS;
  const countsSummary = buildCountsSummary(message);
  const notes = message.replyText?.trim();
  const { items } = useMemo(
    () => buildActivityItems(events, providerName, hasDiagram),
    [events, providerName, hasDiagram],
  );

  return (
    <Message from="assistant" animateIn>
      <MessageContent>
        {message.status === 'working' && events.length === 0 && (
          <ReasoningText phrases={IDLE_PHRASES} className="text-sm" />
        )}
        {events.length > 0 && (
          <AgentActivity
            items={items}
            contentType="step"
            status={message.status ?? 'complete'}
            collapseOnComplete
            summary={
              message.status === 'complete'
                ? countsSummary
                : message.status === 'error'
                  ? 'Generation failed'
                  : message.status === 'aborted'
                    ? 'Stopped'
                    : undefined
            }
            maxHeight={220}
            className="w-full"
          />
        )}
        {message.status === 'complete' && (notes || events.length === 0) && (
          <StreamingResponse status="complete" showActions={false} className="break-words text-[13px]">
            {buildReply(message)}
          </StreamingResponse>
        )}
        {message.status === 'error' && (
          <StreamingResponse
            status="error"
            onRetry={message.retryPayload ? onRetry : undefined}
            className="text-[13px]"
          >
            {message.errorText ?? 'Something went wrong.'}
          </StreamingResponse>
        )}
        {message.status === 'aborted' && (
          <div className="px-1 text-xs text-muted-foreground">Stopped</div>
        )}
      </MessageContent>
    </Message>
  );
};

const AIPanel: React.FC<AIPanelProps> = ({
  open,
  onClose,
  messages,
  isGenerating,
  hasDiagram,
  onSend,
  onStop,
  onRetry,
  onNewChat,
  container,
}) => {
  const [prompt, setPrompt] = useState('');
  // Raw picker value: a provider id, or 'custom:<model id>' for a saved
  // OpenRouter model. Decoded per turn in submit.
  const [selection, setSelection] = useState('');
  const [models, setModels] = useState(() => buildProviderModels());

  // Refresh the model list and default selection each time the panel opens —
  // keys and saved models may have changed in Settings since the last visit.
  useEffect(() => {
    if (!open) return;
    setModels(buildProviderModels());
    const provider = getDefaultProvider();
    if (provider === 'custom') {
      const modelId = getSelectedCustomModel();
      setSelection(encodeModelSelection('custom', modelId || undefined));
    } else {
      setSelection(provider ?? '');
    }
  }, [open]);

  const submit = (value: string, model?: string) => {
    const instruction = value.trim();
    if (!instruction) return;
    setPrompt('');
    const { provider, modelId } = decodeModelSelection(model ?? selection);
    onSend(instruction, provider, modelId);
  };

  const body = (
    <>
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        <div className="p-1.5 bg-purple-500/10 rounded-md">
          <Sparkles className="w-4 h-4 text-purple-500 dark:text-purple-400" />
        </div>
        <span className="flex-1 text-sm font-semibold text-foreground">AI Assistant</span>
        {messages.length > 0 && (
          <Tooltip content="New chat" side="bottom">
            <button
              type="button"
              onClick={onNewChat}
              aria-label="Start a new chat"
              className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
        <Tooltip content="Close (Ctrl+J)" side="bottom">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close AI assistant"
            className="flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </Tooltip>
      </div>

      {/* Transcript */}
      <MessageScroller
        className="flex-1 min-h-0"
        busy={isGenerating}
        contentClassName="flex flex-col gap-4 p-4"
      >
        {messages.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-10 text-center">
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-foreground">
                {hasDiagram ? 'What should change?' : 'Describe your architecture'}
              </p>
              <p className="text-xs text-muted-foreground">
                {hasDiagram
                  ? 'Ask for additions, swaps or removals — the diagram updates in place.'
                  : 'Describe your project, get a diagram.'}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 px-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => submit(s, selection)}
                  className="text-xs px-3 py-1.5 rounded-full border border-border bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) =>
            m.role === 'user' ? (
              <Message key={m.id} from="user" animateIn>
                <MessageContent>
                  <MessageBubble variant="solid" animateIn>
                    <MessageBubbleContent>{m.content}</MessageBubbleContent>
                  </MessageBubble>
                </MessageContent>
              </Message>
            ) : (
              <AssistantTurn
                key={m.id}
                message={m}
                hasDiagram={hasDiagram}
                onRetry={() => onRetry(m)}
              />
            ),
          )
        )}
      </MessageScroller>

      {/* Composer */}
      <div className="border-t border-border p-3">
        <PromptInput
          value={prompt}
          onValueChange={setPrompt}
          models={models}
          model={selection || undefined}
          onModelChange={(value) => {
            setSelection(value);
            // Last model used in chat becomes the next session's default.
            const { provider, modelId } = decodeModelSelection(value);
            if (provider === 'custom' && modelId) setSelectedCustomModel(modelId);
          }}
          onSubmit={submit}
          loading={isGenerating}
          onStop={onStop}
          minRows={2}
          maxRows={8}
          placeholder={
            hasDiagram
              ? 'Ask for changes…'
              : 'Describe your architecture…'
          }
        />
      </div>
    </>
  );

  // Docked in the shell: portal into EditorLayout's slot. The slide-in comes
  // from the shell's dock column, so no width animation here.
  if (container) {
    if (!open) return null;
    return createPortal(
      <aside
        className="flex h-full flex-col bg-surface-secondary"
        style={{ width: '100%', maxWidth: PANEL_WIDTH }}
        aria-label="AI assistant"
      >
        {body}
      </aside>,
      container
    );
  }

  // Inline fallback (demo): a plain flex sibling of the canvas.
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="ai-panel"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: open ? '100%' : 0, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={SPRING_LAYOUT}
          className="h-full shrink-0 overflow-hidden"
          style={{ maxWidth: PANEL_WIDTH }}
        >
          <aside
            className="flex h-full flex-col border-l border-border bg-surface-secondary"
            style={{ width: '100%' }}
            aria-label="AI assistant"
          >
            {body}
          </aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AIPanel;
